'use client'

import { useEffect } from 'react'

import { type IReview } from '@/entities/place'
import {
  type IPlaceIntroduction,
  type IPlaceReviewSummary,
  type IPlaceTopVehicle,
  type IPositiveReviewRate,
  type IProductGroup,
  type ISeoNearbyPlace,
  type ISeoPlace,
} from '@/features/seoPlaceDetail/model'
import PlaceBasicInfo from '@/features/seoPlaceDetail/ui/PlaceBasicInfo/PlaceBasicInfo'
import PlaceBusinessInfo from '@/features/seoPlaceDetail/ui/PlaceBusinessInfo/PlaceBusinessInfo'
import PlaceDataDisclaimer from '@/features/seoPlaceDetail/ui/PlaceDataDisclaimer/PlaceDataDisclaimer'
import PlaceFaq from '@/features/seoPlaceDetail/ui/PlaceFaq/PlaceFaq'
import PlaceMobileCta from '@/features/seoPlaceDetail/ui/PlaceMobileCta/PlaceMobileCta'
import PlaceNearby from '@/features/seoPlaceDetail/ui/PlaceNearby/PlaceNearby'
import PlacePartnerTopNavigation from '@/features/seoPlaceDetail/ui/PlacePartnerTopNavigation/PlacePartnerTopNavigation'
import PlacePcStickyCta from '@/features/seoPlaceDetail/ui/PlacePcStickyCta/PlacePcStickyCta'
import PlacePerformance from '@/features/seoPlaceDetail/ui/PlacePerformance/PlacePerformance'
import PlacePhoto from '@/features/seoPlaceDetail/ui/PlacePhoto/PlacePhoto'
import PlaceProductList from '@/features/seoPlaceDetail/ui/PlaceProductList/PlaceProductList'
import PlacePromotionBanner from '@/features/seoPlaceDetail/ui/PlacePromotionBanner/PlacePromotionBanner'
import PlaceReviewSection from '@/features/seoPlaceDetail/ui/PlaceReviewSection/PlaceReviewSection'
import PlaceTopVehicles from '@/features/seoPlaceDetail/ui/PlaceTopVehicles/PlaceTopVehicles'
import { logEvent } from '@/shared/external/firebase/analytics'
import { CODE, EVENT_NAMES, LABELS, TYPE } from '@/shared/lib'
import { cn } from '@/shared/lib/cn'

import { isPlaceDetailPartnerReservationType } from '../../lib'

interface IPlaceDetailContentProps {
  place: ISeoPlace
  productGroups: IProductGroup[]
  reviews: IReview[]
  nearbyPlaces: ISeoNearbyPlace[]
  introduction: IPlaceIntroduction | null
  reviewSummary: IPlaceReviewSummary | null
  positiveReviewRate: IPositiveReviewRate | null
  topVehicles: IPlaceTopVehicle[]
  reservationType?: string
}

const PlaceDetailContent = ({
  place,
  productGroups,
  reviews,
  nearbyPlaces,
  introduction,
  reviewSummary,
  positiveReviewRate,
  topVehicles,
  reservationType,
}: IPlaceDetailContentProps) => {
  const isPartnerEntry = isPlaceDetailPartnerReservationType(reservationType)
  useEffect(function logPageShow() {
    logEvent(EVENT_NAMES.COMMON_WEB_PLACE.PAGE.SHOW, {
      code: CODE.COMMON_WEB_PLACE.PAGE,
      type: TYPE.PAGE,
      label: LABELS.COMMON_WEB_PLACE.PAGE,
    })
  }, [])

  return (
    <>
      <div className='bg-background-surface-basement'>
        <div
          className={cn(
            'mx-auto min-h-screen w-full max-w-[480px] pb-[120px]',
            // 제휴 유입은 PC도 사이드 카드 없이 모바일과 같은 레이아웃 + 하단 고정 CTA를 쓴다 (WEB-5901)
            !isPartnerEntry &&
              'lg:grid lg:max-w-[1000px] lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10 lg:pb-0'
          )}
        >
          {isPartnerEntry && <PlacePartnerTopNavigation />}
          <main className='flex min-w-0 flex-col'>
            {/* 프로모션 배너 바로 아래에는 어떤 섹션이 와도 섹션 디바이더를 노출하지 않음 (배너는 다음 섹션과 붙어야 함) */}
            <article className='flex flex-col [&>a+[role=separator]]:hidden'>
              <PlacePhoto place={place} />
              <PlaceBasicInfo
                place={place}
                productGroups={productGroups}
                introduction={introduction}
              />
              <PlacePromotionBanner isPartnerEntry={isPartnerEntry} />
              {!isPartnerEntry && (
                <PlaceProductList productGroups={productGroups} />
              )}
              <PlacePerformance
                introduction={introduction}
                isPartnerEntry={isPartnerEntry}
              />
              <PlaceReviewSection
                place={place}
                reviews={reviews}
                reviewSummary={reviewSummary}
                positiveReviewRate={positiveReviewRate}
                isPartnerEntry={isPartnerEntry}
              />
              <PlaceTopVehicles cars={topVehicles} />
              <PlaceBusinessInfo place={place} />
            </article>
            {!isPartnerEntry && <PlaceNearby nearbyPlaces={nearbyPlaces} />}
            <PlaceFaq place={place} productGroups={productGroups} />
            <PlaceDataDisclaimer />
          </main>
          {!isPartnerEntry && (
            <aside className='hidden lg:block'>
              <PlacePcStickyCta place={place} productGroups={productGroups} />
            </aside>
          )}
        </div>
      </div>
      <PlaceMobileCta isPartnerEntry={isPartnerEntry} />
    </>
  )
}

export default PlaceDetailContent
